import {
  BackEndValueType,
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IKType
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import isEqual from "react-fast-compare";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { knownTypes } from "../../../../shared/utility/designer-symbol-picker-engine";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import ActionParameterTitle from "./action-parameter-title";
import SymbolDropdownLoading from "./symbol-dropdown-loading";

export interface IFetchFromCacheSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  onSaveAction: (action: IAction, changeReason?: ESdChangeReason) => void;
  /**
   * If PushToCache action is in the same screen, it will be passed here
   * So we can update the return type of FetchFromCache actions when PushToCache action is updated
   */
  pushToCacheAction: IAction | null;
}

export interface ICacheInfo {
  returnTypeName: string | null;
  returnCardinality: ECardinality | null;
  typeModel: IKType | null;
  path: string | null;
}

export type Caches = { [key: string]: ICacheInfo };

export interface IFetchFromCacheSymbolsGroupState {}

class FetchFromCacheSymbolsGroup extends PureSdComponent<
  IFetchFromCacheSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IFetchFromCacheSymbolsGroupState
> {
  constructor(props: IFetchFromCacheSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  async componentDidMount() {
    if (!this.props.pushToCacheAction) {
      await this.props.sd.fetchDynamicTypeKeys();
      this.updateReturnTypesFromCacheKey(this.props.sd.caches);
    }
  }

  async componentDidUpdate(prevProps: IFetchFromCacheSymbolsGroupProps) {
    if (
      this.props.pushToCacheAction &&
      this.getCacheKeyValue(this.props.parameters) === this.getCacheKeyValue(this.props.pushToCacheAction.parameters) &&
      !isEqual(prevProps.pushToCacheAction?.parameters, this.props.pushToCacheAction?.parameters)
    ) {
      try {
        const { caches } = await this.props.sd.getLocalDynamicTypes(true);
        this.updateReturnTypesFromCacheKey(caches);
      } catch (error) {
        console.error("Failed to update caches in componentDidUpdate:", error);
      }
    }
  }

  /**
   * Navigate through typeModel using path to get the nested type model
   * Similar to backend's GetReturnTypeModelByFieldName
   */
  private getTypeModelByPath = (typeModel: IKType | null | undefined, path: string | null): IKType | undefined => {
    if (!typeModel || !path) return typeModel || undefined;

    const fields = path.split(".");
    if (fields.length === 0) return typeModel;

    let currentModel = typeModel;
    for (let i = 0; i < fields.length; i++) {
      const fieldNameWithoutModifier = fields[i].split("[")[0];
      const field = currentModel.elements?.find((x) => x.name === fieldNameWithoutModifier);

      if (!field) return undefined;

      // If this is the last field, return its elementTypeModel (the nested type)
      if (i === fields.length - 1) {
        return field.elementTypeModel;
      }

      // Otherwise, navigate deeper
      if (field.elementTypeModel) {
        currentModel = field.elementTypeModel;
      } else {
        return undefined;
      }
    }

    return currentModel;
  };

  updateReturnTypesFromCacheKey = (caches: Caches) => {
    const cacheKeyValue = this.getCacheKeyValue(this.props.parameters);
    if (!cacheKeyValue || !caches[cacheKeyValue]) return;

    const action: IAction = _.cloneDeep(this.props.action);
    const cacheInfo = caches[cacheKeyValue];

    // If path exists, navigate to get the correct nested typeModel
    const resolvedTypeModel = cacheInfo.path
      ? this.getTypeModelByPath(cacheInfo.typeModel, cacheInfo.path)
      : cacheInfo.typeModel;

    if (
      action.returnType !== cacheInfo.returnTypeName ||
      action.returnCardinality !== cacheInfo.returnCardinality ||
      !isEqual(action.returnTypeModel, resolvedTypeModel)
    ) {
      action.returnType = cacheInfo.returnTypeName as BackEndValueType;
      action.returnCardinality = cacheInfo.returnCardinality as ECardinality;

      if (knownTypes.includes(action.returnType?.toLowerCase())) {
        action.returnTypeModel = undefined;
      } else {
        action.returnTypeModel = resolvedTypeModel as IKType;
      }

      action.tag = cacheKeyValue;

      this.props.onSaveAction(action, ESdChangeReason.ChangeSelectedDiByTools);
    }
  };

  handleCacheKeySelect = (value: string | null): void => {
    // Clearing the field is reported through `onClear`, which writes the same parameter. The
    // selectbox also reports the empty selection here, and acting on it would save twice and
    // put a null in the tree on the way past.
    if (value === null || value === undefined) return;
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = value;
    keyParam.symbol.category = ESymbolCategory.fixed;

    const caches = this.props.sd.caches;
    const cacheInfo = caches[value];

    // If path exists, navigate to get the correct nested typeModel
    const resolvedTypeModel = cacheInfo.path
      ? this.getTypeModelByPath(cacheInfo.typeModel, cacheInfo.path)
      : cacheInfo.typeModel;

    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    action.returnType = cacheInfo?.returnTypeName as BackEndValueType;
    action.returnCardinality = cacheInfo?.returnCardinality as ECardinality;

    if (knownTypes.includes(action.returnType?.toLowerCase())) {
      action.returnTypeModel = undefined;
    } else {
      action.returnTypeModel = resolvedTypeModel as IKType;
    }

    action.tag = value;

    this.props.onSaveAction(action, ESdChangeReason.ActionParameterFixedValueChanged);
  };

  handleCacheKeyClear = () => {
    const params = _.cloneDeep(this.props.parameters);
    const keyParam = params.find((p) => p.name === "key");
    if (!keyParam) return;
    if (!keyParam.symbol) keyParam.symbol = KMainFunctions.getDefaultSymbol(keyParam.Typename);
    keyParam.symbol.fixedValue = undefined;
    keyParam.symbol.category = ESymbolCategory.fixed;
    const action: IAction = _.cloneDeep(this.props.action);
    action.parameters = params;
    action.returnType = "Any";
    action.returnCardinality = ECardinality.Any;
    // The tag is the key; left behind, it would label the result with a key it no longer reads.
    action.tag = undefined;

    this.props.onSaveAction(action, ESdChangeReason.ChangeSelectedDiByTools);
  };

  getCacheKeyValue = (parameters: IActionParameter[]): string => {
    const keyParam = parameters.find((p) => p.name === "key");
    return keyParam?.symbol?.fixedValue;
  };

  renderKey() {
    return (
      <div>
        <div className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: "6px", marginTop: "8px" }}>
          <ActionParameterTitle parameter={{ name: "Cache Key", Typename: "String", cardinality: ECardinality.One }} />
        </div>
        <KuikaSelectbox
          searchable
          size="sm"
          allowClear={
            !this.props.sd.isDynamicTypesLoading &&
            !!this.getCacheKeyValue(this.props.parameters) &&
            this.getCacheKeyValue(this.props.parameters)?.trim?.() !== ""
          }
          style={{ width: "100%" }}
          value={this.getCacheKeyValue(this.props.parameters)}
          onVisibleChange={(open: boolean): void => {
            if (open) this.props.sd.fetchDynamicTypeKeys();
          }}
          onChange={this.handleCacheKeySelect}
          onClear={this.handleCacheKeyClear}
          options={(Object.keys(this.props.sd.caches) || []).map((k) => ({ label: k, value: k }))}
          placeholder="Select Cache Key"
          className="kuika_add_actions_inputs_size"
          notFoundContent={this.props.sd.isDynamicTypesLoading ? <SymbolDropdownLoading /> : undefined}
        />
      </div>
    );
  }

  render() {
    return <div className="kuika_property-grid-column">{this.renderKey()}</div>;
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    );
  };
}

const fetchFromCacheSymbolsGroup = withContext<IFetchFromCacheSymbolsGroupProps>(
  withSD<IFetchFromCacheSymbolsGroupProps>(FetchFromCacheSymbolsGroup)
);
export { fetchFromCacheSymbolsGroup as FetchFromCacheSymbolsGroup };
